import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IoBanOutline,
  IoNotificationsOutline,
  IoCreateOutline,
  IoPauseOutline,
} from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';

import PagePanel from 'src/components/PagePanel';
import { Container } from 'src/components/Container';
import { ContentSkeleton } from 'src/components/ContentSkeleton';
import Badge from 'src/components/Table/Row/Badge';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { ConfirmModal } from 'src/components/ConfirmModal';
import { SendNotificationModal } from 'src/components/SendNotificationModal';
import { useAdminPermissions } from 'src/hooks/useAdminPermissions';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { PERMISSIONS } from 'src/utils/permissions';
import { ROUTE_USERS } from 'src/utils/routes';
import {
  UserSummary,
  fetchPlatformUserSummary,
  formatKycStatusLabel,
  formatUserDisplayName,
  formatUserStatusLabel,
  updatePlatformUserStatus,
} from 'src/api/adminUsers';

import DetailsTab from './tabs/DetailsTab';
import KycTab from './tabs/KycTab';
import DepositsTab from './tabs/DepositsTab';
import TransactionsTab from './tabs/TransactionsTab';
import PortfolioTab from './PortfolioTab';
import WalletsTab from './tabs/WalletsTab';
import ActivityTab from './tabs/ActivityTab';
import UserStatusModal from './components/UserStatusModal';
import {
  ActionIconButton,
  BalanceBlock,
  BalanceHint,
  BalanceLabel,
  BalanceValue,
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbSep,
  DetailBadges,
  DetailHeader,
  DetailTitle,
  TabActions,
  TabsRow,
  UsersTab,
  UsersTabs,
} from './styled';

type DetailTab = 'details' | 'kyc' | 'deposits' | 'transactions' | 'portfolio' | 'wallets' | 'activity';
type QuickStatus = 'SUSPENDED' | 'BANNED';

const formatMoney = (total?: string, currency?: string) => {
  // Amounts from the API are decimal strings — display as-is (no float parse).
  const code = (currency || 'USD').toUpperCase();
  const symbol = code === 'EUR' ? '€' : code === 'USD' ? '$' : `${code} `;
  const amount = total != null && String(total).trim() !== '' ? String(total) : '0';
  return `${symbol}${amount}`;
};

const UserDetail = () => {
  const { userId = '' } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { can } = useAdminPermissions();

  const [summary, setSummary] = useState<UserSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>('details');
  // Set when jumping from a wallet row to that wallet's transaction history.
  const [txAddressFilter, setTxAddressFilter] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<QuickStatus | null>(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  // Tabs that returned 403 — hide instead of toasting (per Users API notes).
  const [blockedTabs, setBlockedTabs] = useState<Partial<Record<DetailTab, boolean>>>({});

  const canViewKyc = can(PERMISSIONS.KYC_REVIEW);
  const canWrite = can(PERMISSIONS.USER_WRITE);

  const markTabForbidden = useCallback((tab: DetailTab) => {
    setBlockedTabs((prev) => (prev[tab] ? prev : { ...prev, [tab]: true }));
  }, []);

  const loadSummary = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setForbidden(false);
    try {
      const data = await fetchPlatformUserSummary(userId);
      setSummary(data);
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 403) {
        setForbidden(true);
        setSummary(null);
      } else {
        dispatch(
          setAppAlert({
            message: e?.response?.data?.error ?? 'Failed to load user',
            isSuccess: false,
          }),
        );
        setSummary(null);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, dispatch]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    setBlockedTabs({});
  }, [userId]);

  // Each tab keeps `onForbidden` in its data-loading useCallback's deps, so an
  // inline arrow here would change identity on every render of this page and
  // re-trigger that fetch. Any toast re-renders the whole route tree (App
  // subscribes to the alert state), which made copying a value reload the tab.
  const forbiddenHandlers = useMemo(
    () => ({
      details: () => markTabForbidden('details'),
      kyc: () => markTabForbidden('kyc'),
      deposits: () => markTabForbidden('deposits'),
      transactions: () => markTabForbidden('transactions'),
      wallets: () => markTabForbidden('wallets'),
      activity: () => markTabForbidden('activity'),
    }),
    [markTabForbidden],
  );

  const viewWalletTransactions = useCallback((address: string) => {
    setTxAddressFilter(address);
    setActiveTab('transactions');
  }, []);

  const tabs = useMemo(() => {
    const all: { id: DetailTab; label: string; visible: boolean }[] = [
      { id: 'details', label: 'Details', visible: !blockedTabs.details },
      { id: 'kyc', label: 'KYC', visible: canViewKyc && !blockedTabs.kyc },
      { id: 'deposits', label: 'Deposits', visible: !blockedTabs.deposits },
      { id: 'transactions', label: 'Transactions', visible: !blockedTabs.transactions },
      { id: 'portfolio', label: 'Portfolio', visible: !blockedTabs.portfolio },
      { id: 'wallets', label: 'Wallets', visible: !blockedTabs.wallets },
      { id: 'activity', label: 'Activity', visible: !blockedTabs.activity },
    ];
    return all.filter((tab) => tab.visible);
  }, [canViewKyc, blockedTabs]);

  const canViewTransactions = tabs.some((tab) => tab.id === 'transactions');

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0]?.id ?? 'details');
    }
  }, [tabs, activeTab]);

  const applyQuickStatus = async (status: QuickStatus) => {
    if (!userId || !canWrite) return;
    setStatusBusy(true);
    try {
      await updatePlatformUserStatus(userId, status);
      dispatch(
        setAppAlert({
          message: `User marked as ${formatUserStatusLabel(status)}`,
          isSuccess: true,
        }),
      );
      setConfirmStatus(null);
      await loadSummary();
    } catch (e: any) {
      dispatch(
        setAppAlert({
          message: e?.response?.data?.error ?? 'Failed to update status',
          isSuccess: false,
        }),
      );
    } finally {
      setStatusBusy(false);
    }
  };

  const user = summary?.user;
  const displayName = formatUserDisplayName(user);
  const unpricedCount = summary?.balance?.unpriced?.length ?? 0;
  const kycBadge = user?.kyc_status ? formatKycStatusLabel(user.kyc_status) : null;
  const userStatus = String(user?.status || '').toUpperCase();
  const isSuspended = userStatus === 'SUSPENDED';
  const isBanned = userStatus === 'BANNED';
  const pauseDisabled = statusBusy || isSuspended || isBanned;
  const banDisabled = statusBusy || isBanned;

  if (loading) {
    return (
      <PagePanel>
        <ContentSkeleton variant="userDetail" />
      </PagePanel>
    );
  }

  if (forbidden) {
    return (
      <PagePanel>
        <Breadcrumb>
          <BreadcrumbLink type="button" onClick={() => navigate(ROUTE_USERS)}>
            Users
          </BreadcrumbLink>
          <BreadcrumbSep>&gt;</BreadcrumbSep>
          <span>Restricted</span>
        </Breadcrumb>
        <Container padding="2rem 0">
          You do not have permission to view this user.
        </Container>
      </PagePanel>
    );
  }

  if (!summary || !user) {
    return (
      <PagePanel>
        <Breadcrumb>
          <BreadcrumbLink type="button" onClick={() => navigate(ROUTE_USERS)}>
            Users
          </BreadcrumbLink>
          <BreadcrumbSep>&gt;</BreadcrumbSep>
          <span>Not found</span>
        </Breadcrumb>
        <Container padding="2rem 0">User not found.</Container>
      </PagePanel>
    );
  }

  return (
    <PagePanel>
      <Breadcrumb>
        <BreadcrumbLink type="button" onClick={() => navigate(ROUTE_USERS)}>
          Users
        </BreadcrumbLink>
        <BreadcrumbSep>&gt;</BreadcrumbSep>
        <span>{displayName}</span>
      </Breadcrumb>

      <DetailHeader>
        <div>
          <DetailTitle>{displayName}</DetailTitle>
          <DetailBadges>
            {user.status && <Badge value={user.status} />}
            {kycBadge && <Badge value={kycBadge} />}
          </DetailBadges>
        </div>
        <BalanceBlock>
          <BalanceLabel>Total Balance:</BalanceLabel>
          <BalanceValue>{formatMoney(summary.balance.total, summary.balance.currency)}</BalanceValue>
          {unpricedCount > 0 && (
            <BalanceHint>
              + {unpricedCount} asset{unpricedCount === 1 ? '' : 's'} not priced
            </BalanceHint>
          )}
        </BalanceBlock>
      </DetailHeader>

      <TabsRow>
        <UsersTabs>
          {tabs.map((tab) => (
            <UsersTab
              key={tab.id}
              type="button"
              $active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </UsersTab>
          ))}
        </UsersTabs>
        <PermissionGuard permission={PERMISSIONS.USER_WRITE}>
          <TabActions>
            <ActionIconButton
              type="button"
              aria-label="Edit user"
              title="Edit user"
              onClick={() => setEditOpen(true)}
            >
              <IoCreateOutline size={16} />
            </ActionIconButton>
            <ActionIconButton
              type="button"
              aria-label="Suspend user"
              title={
                isBanned
                  ? 'User is banned'
                  : isSuspended
                    ? 'Already Suspicious'
                    : 'Mark as Suspicious'
              }
              disabled={pauseDisabled}
              onClick={() => setConfirmStatus('SUSPENDED')}
            >
              <IoPauseOutline size={16} />
            </ActionIconButton>
            <ActionIconButton
              type="button"
              aria-label="Ban user"
              title={isBanned ? 'Already banned' : 'Ban user'}
              disabled={banDisabled}
              onClick={() => setConfirmStatus('BANNED')}
            >
              <IoBanOutline size={16} />
            </ActionIconButton>
            <ActionIconButton
              type="button"
              aria-label="Send notification"
              title="Send notification"
              onClick={() => setNotifyOpen(true)}
            >
              <IoNotificationsOutline size={16} />
            </ActionIconButton>
          </TabActions>
        </PermissionGuard>
      </TabsRow>

      {activeTab === 'details' && (
        <DetailsTab userId={userId} summary={summary} onForbidden={forbiddenHandlers.details} />
      )}
      {activeTab === 'kyc' && canViewKyc && !blockedTabs.kyc && (
        <KycTab userId={userId} user={user} onForbidden={forbiddenHandlers.kyc} />
      )}
      {activeTab === 'deposits' && !blockedTabs.deposits && (
        <DepositsTab userId={userId} onForbidden={forbiddenHandlers.deposits} />
      )}
      {activeTab === 'transactions' && !blockedTabs.transactions && (
        <TransactionsTab
          userId={userId}
          initialSearch={txAddressFilter}
          onForbidden={forbiddenHandlers.transactions}
        />
      )}
      {activeTab === 'portfolio' && <PortfolioTab summary={summary} />}
      {activeTab === 'wallets' && !blockedTabs.wallets && (
        <WalletsTab
          userId={userId}
          onForbidden={forbiddenHandlers.wallets}
          onViewTransactions={canViewTransactions ? viewWalletTransactions : undefined}
        />
      )}
      {activeTab === 'activity' && !blockedTabs.activity && (
        <ActivityTab userId={userId} onForbidden={forbiddenHandlers.activity} />
      )}

      {notifyOpen && userId && (
        <SendNotificationModal
          target={{ kind: 'individual', userId, userLabel: displayName }}
          onClose={() => setNotifyOpen(false)}
        />
      )}

      {editOpen && (
        <UserStatusModal
          user={user}
          onClose={() => setEditOpen(false)}
          onUpdated={() => {
            setEditOpen(false);
            loadSummary();
          }}
        />
      )}

      {confirmStatus && (
        <ConfirmModal
          title={confirmStatus === 'BANNED' ? 'Ban user' : 'Mark as Suspicious'}
          message={
            confirmStatus === 'BANNED' ? (
              <>
                Ban <strong>{displayName}</strong>? This will set status to BANNED when confirmed.
              </>
            ) : (
              <>
                Mark <strong>{displayName}</strong> as Suspicious? This will set status to
                SUSPENDED.
              </>
            )
          }
          confirmText={confirmStatus === 'BANNED' ? 'Ban' : 'Confirm'}
          confirmClassName="negative"
          onClose={() => setConfirmStatus(null)}
          onConfirm={() => applyQuickStatus(confirmStatus)}
        />
      )}
    </PagePanel>
  );
};

export default UserDetail;
